import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { BreadCrumb, TableContainer } from "@/components";
import { PATH_NAME } from "@/constants";
import { getRedemptions } from "@/features";
import { RedemptionType } from "@/types";
import { formatCurrency } from "@/utils";
import { ColumnDef } from "@tanstack/react-table";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
} from "reactstrap";
import { CreateOrEditRedemption } from "./components/CreateOrEditRedemption";
import { DeleteRedemption } from "./components/DeleteRedemption";

const RedemptionManagement = () => {
  const { redemptionList } = useAppSelector((state) => state.redemption);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const columns = useMemo<ColumnDef<RedemptionType>[]>(
    () => [
      {
        header: "Offer Name",
        accessorKey: "promotion_name",
        disableFilters: false,
        enableColumnFilter: false,
      },
      {
        header: "Start Date",
        accessorKey: "created_at",
        disableFilters: true,
        enableColumnFilter: false,
        cell: ({ row }) => moment(row.original.created_at).format("DD/MM/YYYY"),
      },
      {
        header: "Finish Date",
        accessorKey: "end_date",
        disableFilters: true,
        enableColumnFilter: false,
        cell: ({ row }) => moment(row.original.end_date).format("DD/MM/YYYY"),
      },
      {
        header: "Package",
        accessorKey: "package_name",
        disableFilters: true,
        enableColumnFilter: false,
      },
      {
        header: "Price",
        accessorKey: "discount_price",
        disableFilters: true,
        enableColumnFilter: false,
        sortingFn: "alphanumeric",
        cell: ({ row }) => formatCurrency(row.original.discount_price || 0),
      },
      {
        header: "Total Codes",
        accessorKey: "total_code",
        disableFilters: true,
        enableColumnFilter: false,
        headerClass: "text-center",
        rowClass: "text-center",
      },
      {
        header: "Used Codes",
        accessorKey: "used_code",
        enableColumnFilter: false,
        headerClass: "text-center",
        rowClass: "text-center",
      },
      {
        header: "Status",
        accessorKey: "status",
        disableFilters: true,
        enableColumnFilter: false,
        cell: ({ row }) => {
          return (
            <Badge
              color={row.original.status ? "success" : "danger"}
              style={{ fontSize: 13 }}
            >
              {row.original.status ? "Active" : "Inactive"}
            </Badge>
          );
        },
      },
      {
        header: "Action",
        disableFilters: true,
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <div>
              <Button
                color="primary"
                className="btn-sm"
                style={{ minWidth: 58 }}
                onClick={() =>
                  navigate(
                    `${PATH_NAME.REDEMPTION_MANAGEMENT}/view/${row.original.id}`,
                  )
                }
              >
                View
              </Button>
              <Button
                color="primary"
                className="btn-sm ms-2"
                style={{ minWidth: 58 }}
                onClick={() => {
                  setSelectedId(row.original.id as number);
                  setIsOpen(true);
                }}
              >
                Edit
              </Button>
              <Button
                color="danger"
                className="btn-sm ms-2"
                style={{ minWidth: 58 }}
                onClick={() => {
                  setSelectedId(row.original.id as number);
                  setIsDelete(true);
                }}
              >
                Delete
              </Button>
            </div>
          );
        },
      },
    ],
    [navigate],
  );

  const getData = useCallback(async () => {
    setIsLoading(true);
    try {
      await dispatch(getRedemptions()).unwrap();
    } catch (error) {
      console.error("error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Redemption Management" />
          <Card>
            <CardHeader className="text-end">
              <Button color="primary" onClick={() => setIsOpen(true)}>
                <span>
                  <i className="ri-add-line align-bottom me-1" />
                  Create New Redemption
                </span>
              </Button>
            </CardHeader>
            <CardBody>
              <TableContainer
                columns={columns || []}
                data={redemptionList || []}
                customPageSize={10}
                isLoading={isLoading}
                tableClass="table-centered align-middle table-nowrap mb-0"
                theadClass="text-muted table-light"
                divClass="table-responsive"
              />
            </CardBody>
          </Card>
        </Container>
      </div>

      {isOpen && (
        <CreateOrEditRedemption
          isOpen={isOpen}
          selectedId={selectedId as number}
          onSuccess={() => getData()}
          toggle={() => {
            setIsOpen(false);
            setSelectedId(null);
          }}
        />
      )}

      {isDelete && (
        <DeleteRedemption
          isOpen={isDelete}
          selectedId={selectedId as number}
          onSuccess={() => getData()}
          toggle={() => {
            setIsDelete(false);
            setSelectedId(null);
          }}
        />
      )}
    </>
  );
};

export default RedemptionManagement;
